import { useMemo, useState } from "react";

const abpDataHeaders = [
    { key: 'uprn', label: 'UPRN' },
    { key: 'classification_code', label: 'Classification Code' },
    { key: 'organisation', label: 'Organisation' },
    { key: 'sub_building', label: 'Sub Building' },
    { key: 'building_name', label: 'Building Name' },
    { key: 'building_number', label: 'Building Number' },
    { key: 'street_name', label: 'Street Name' },
    { key: 'locality', label: 'Locality' },
    { key: 'town_name', label: 'Town Name' },
    { key: 'postcode', label: 'Postcode' },
]

const EMPTY_ROW_COUNT = 7;

const abpDataKeyMap = {
    uprn: 'UPRN',
    classification_code: 'CLASSIFICATION_CODE',
    organisation: 'ORGANISATION',
    sub_building: 'SUB_BUILDING',
    building_name: 'BUILDING_NAME',
    building_number: 'BUILDING_NUMBER',
    street_name: 'STREET_NAME',
    locality: 'LOCALITY',
    town_name: 'TOWN_NAME',
    postcode: 'POSTCODE',
};

export default function ABPDataTable({ results = [], translations }) {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const sortedResults = useMemo(() => {
        if (results.length === 0 || !sortConfig.key) {
            return results;
        }

        const rowKey = abpDataKeyMap[sortConfig.key];

        return [...results].sort((a, b) => {
            const aValue = a?.[rowKey] ?? '';
            const bValue = b?.[rowKey] ?? '';

            const comparison = String(aValue).localeCompare(String(bValue), undefined, {
                numeric: true,
                sensitivity: 'base'
            });

            return sortConfig.direction === 'asc' ? comparison : -comparison;
        });
    }, [results, sortConfig]);

    const rows = sortedResults.length > 0 ? sortedResults : new Array(EMPTY_ROW_COUNT).fill(null);

    const handleSort = (key) => {
        setSortConfig((current) => {
            if (current.key === key) {
                return {
                    key,
                    direction: current.direction === 'asc' ? 'desc' : 'asc'
                };
            }

            return { key, direction: 'asc' };
        });
    };

    const handleSortKeyDown = (e, key) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSort(key);
        }
    };

    return (
        <table className="table borders smaller-text">
            <thead>
                <tr>
                    {abpDataHeaders.map(header => {
                        const isSorted = sortConfig.key === header.key;
                        const chevronClass = isSorted
                            ? `sort-chevron ${sortConfig.direction === 'asc' ? 'sort-chevron-up' : 'sort-chevron-down'}`
                            : '';

                        return (
                            <th
                                key={header.key}
                                onClick={() => handleSort(header.key)}
                                onKeyDown={(e) => handleSortKeyDown(e, header.key)}
                                role="button"
                                tabIndex={0}
                                aria-sort={isSorted ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                                className="sortable-header"
                            >
                                <span>{translations[header.key] ?? header.label}</span>
                                {isSorted && <span className={chevronClass} aria-hidden="true" />}
                            </th>
                        );
                    })}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, index) => (
                    <tr key={row ? row[abpDataKeyMap.uprn] : index}>
                        {abpDataHeaders.map(header => (
                            <td key={header.key}>{row ? row[abpDataKeyMap[header.key]] : ''}</td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}
